import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CalendarOutlined,
  CarOutlined,
  ContainerOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  LinkOutlined,
  NodeIndexOutlined,
  NumberOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../services/axiosInstance";
import {
  DELETE_RAKE,
  GET_RAKE,
  WORKFLOW_STATS,
} from "../../services/apiEndPoints";
import { downloadRakeReport } from "../../services/rakeReport";
import { IRakeDetail, IRakeMaster } from "../types/rake.types";
import { IWorkflowStats, IWorkflowUpload } from "../types/workflow.types";
import RakeDriversList from "./RakeDriversList";
import VehicleUploadsModal from "./VehicleUploadsModal";
import RakeDocumentsModal from "./RakeDocumentsModal";
import TripListComponent from "../trip/TripListComponent";
import { getDocTypeConfig } from "./docTypeLabels";
import { toastSuccess, toastError } from "../../utils/toast";

const { Title, Text } = Typography;

const RakeDetailsComponent = () => {
  const { rakeMasterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rakeMaster, setRakeMaster] = useState<IRakeMaster | null>(null);
  const [rakeDetails, setRakeDetails] = useState<IRakeDetail[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [uploads, setUploads] = useState<IWorkflowUpload[]>([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [docsOpen, setDocsOpen] = useState(false);

  const fetchRake = useCallback(async () => {
    if (!rakeMasterId) return;
    try {
      setLoading(true);
      const response = await axios.get(GET_RAKE(rakeMasterId));
      const data = response?.data?.data ?? response?.data;
      setRakeMaster(data?.rakeMaster ?? null);
      setRakeDetails(
        Array.isArray(data?.rakeDetails) ? data.rakeDetails : []
      );
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        toastError("Rake master not found");
      } else {
        toastError("Failed to fetch rake");
      }
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId]);

  useEffect(() => {
    fetchRake();
  }, [fetchRake]);

  const fetchUploads = useCallback(async () => {
    if (!rakeMaster) return;
    setUploadsLoading(true);
    try {
      const fromDate = dayjs(rakeMaster.createdAt).format("YYYY-MM-DD");
      const toDate = dayjs(rakeMaster.updatedAt || new Date()).format(
        "YYYY-MM-DD"
      );
      const res = await axios.get(
        `${WORKFLOW_STATS}?fromDate=${fromDate}&toDate=${toDate}`
      );
      const wf: IWorkflowStats | undefined = res?.data?.data;
      const startedAt = dayjs(rakeMaster.createdAt);
      const endedAt = rakeMaster.updatedAt
        ? dayjs(rakeMaster.updatedAt)
        : null;
      const filtered = (wf?.uploads || []).filter((u) => {
        const t = dayjs(u.createdAt);
        if (!t.isAfter(startedAt)) return false;
        if (endedAt && t.isAfter(endedAt)) return false;
        return true;
      });
      setUploads(filtered);
    } catch {
      setUploads([]);
    } finally {
      setUploadsLoading(false);
    }
  }, [rakeMaster]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  const openVehicleModal = useCallback((vehicleNumber: string) => {
    if (!vehicleNumber) return;
    setSelectedVehicle(vehicleNumber);
    setVehicleModalOpen(true);
  }, []);

  const closeVehicleModal = useCallback(() => {
    setVehicleModalOpen(false);
  }, []);

  const vehicleSummaries = useMemo(() => {
    const map = new Map<
      string,
      {
        vehicleNumber: string;
        count: number;
        firstAt: string;
        lastAt: string;
        docTypes: Record<string, number>;
      }
    >();
    uploads.forEach((u) => {
      if (!u.vehicleNumber) return;
      const existing = map.get(u.vehicleNumber);
      if (!existing) {
        map.set(u.vehicleNumber, {
          vehicleNumber: u.vehicleNumber,
          count: 1,
          firstAt: u.createdAt,
          lastAt: u.createdAt,
          docTypes: { [u.docType]: 1 },
        });
        return;
      }
      existing.count += 1;
      existing.docTypes[u.docType] = (existing.docTypes[u.docType] || 0) + 1;
      if (dayjs(u.createdAt).isBefore(existing.firstAt))
        existing.firstAt = u.createdAt;
      if (dayjs(u.createdAt).isAfter(existing.lastAt))
        existing.lastAt = u.createdAt;
    });
    return Array.from(map.values()).sort(
      (a, b) => dayjs(b.lastAt).unix() - dayjs(a.lastAt).unix()
    );
  }, [uploads]);

  const handleDownloadReport = useCallback(async () => {
    if (!rakeMasterId) return;
    setReportLoading(true);
    try {
      await downloadRakeReport({ rakeMasterId });
      toastSuccess("Rake report downloaded");
    } catch (error: any) {
      toastError(error?.message || "Failed to download rake report");
    } finally {
      setReportLoading(false);
    }
  }, [rakeMasterId]);

  const handleDelete = useCallback(() => {
    if (!rakeMasterId) return;
    Modal.confirm({
      title: "Delete Rake",
      content:
        "Delete this rake? This removes the master and all its wagon details.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await axios.delete(DELETE_RAKE(rakeMasterId));
          toastSuccess("Rake deleted successfully");
          navigate("/rakes");
        } catch {
          toastError("Failed to delete rake");
        }
      },
    });
  }, [rakeMasterId, navigate]);

  const totals = useMemo(() => {
    return rakeDetails.reduce(
      (acc, r) => {
        acc.totalBags += Number(r.totalBags || 0);
        acc.rrBags += Number(r.rrBags || 0);
        acc.chargeWeight += Number(r.chargeWeight || 0);
        acc.totalWeight += Number(r.totalWeight || 0);
        return acc;
      },
      { totalBags: 0, rrBags: 0, chargeWeight: 0, totalWeight: 0 }
    );
  }, [rakeDetails]);

  const shortageWeight = totals.chargeWeight - totals.totalWeight;

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        dataIndex: "sno",
        key: "sno",
        width: 70,
        sorter: (a: IRakeDetail, b: IRakeDetail) => a.sno - b.sno,
      },
      {
        title: "Wagon No",
        dataIndex: "wagonNo",
        key: "wagonNo",
        render: (wagonNo: string) =>
          wagonNo ? (
            <Text strong style={{ fontSize: 13 }}>
              {wagonNo}
            </Text>
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
      {
        title: "Type",
        dataIndex: "typeOfWagon",
        key: "typeOfWagon",
        render: (type: string) =>
          type ? <Tag color="geekblue">{type}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Variety",
        dataIndex: "variety",
        key: "variety",
        render: (variety: string) =>
          variety ? <Tag color="purple">{variety}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Total Bags",
        dataIndex: "totalBags",
        key: "totalBags",
        align: "right" as const,
        render: (v: number) => v?.toLocaleString() ?? 0,
      },
      {
        title: "RR Bags",
        dataIndex: "rrBags",
        key: "rrBags",
        align: "right" as const,
        render: (v: number) => v?.toLocaleString() ?? 0,
      },
      {
        title: "Spillage",
        dataIndex: "spillageBags",
        key: "spillageBags",
        align: "right" as const,
        render: (v: number) => v?.toLocaleString() ?? 0,
      },
      {
        title: "Charge Weight (kg)",
        dataIndex: "chargeWeight",
        key: "chargeWeight",
        align: "right" as const,
        render: (v: number) => `${(v ?? 0).toLocaleString()} kg`,
      },
      {
        title: "Total Weight (kg)",
        dataIndex: "totalWeight",
        key: "totalWeight",
        align: "right" as const,
        render: (v: number) => `${(v ?? 0).toLocaleString()} kg`,
      },
      {
        title: "Shortage (kg)",
        key: "shortage",
        align: "right" as const,
        render: (_: any, r: IRakeDetail) => {
          const diff = Number(r.chargeWeight || 0) - Number(r.totalWeight || 0);
          if (!diff) return <Text type="secondary">0 kg</Text>;
          return (
            <Text style={{ color: diff > 0 ? "#dc2626" : "#10b981" }}>
              {diff.toLocaleString()} kg
            </Text>
          );
        },
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        render: (remarks: string) =>
          remarks ? (
            <Text style={{ fontSize: 13 }}>{remarks}</Text>
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading rake...</Text>
        </div>
      </div>
    );
  }

  if (!rakeMaster) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/rakes")}
          style={{ marginBottom: 16 }}
        >
          Back to Rakes
        </Button>
        <Card>
          <div style={{ textAlign: "center", padding: 40 }}>
            <ContainerOutlined
              style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
            />
            <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
              Rake not found
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const summaryCards = [
    {
      label: "Wagons",
      value: rakeDetails.length,
      color: "#1677ff",
      bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
      border: "#93c5fd",
    },
    {
      label: "Total Bags",
      value: totals.totalBags.toLocaleString(),
      color: "#8b5cf6",
      bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
      border: "#c4b5fd",
    },
    {
      label: "RR Bags",
      value: totals.rrBags.toLocaleString(),
      color: "#10b981",
      bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
      border: "#6ee7b7",
    },
    {
      label: "Total Weight (kg)",
      value: `${totals.totalWeight.toLocaleString()} kg`,
      color: "#f59e0b",
      bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
      border: "#fcd34d",
    },
    {
      label: "Charge Weight (kg)",
      value: `${totals.chargeWeight.toLocaleString()} kg`,
      color: "#0ea5e9",
      bg: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
      border: "#7dd3fc",
    },
    {
      label: "Shortage (kg)",
      value: `${shortageWeight.toLocaleString()} kg`,
      color: shortageWeight > 0 ? "#dc2626" : "#10b981",
      bg:
        shortageWeight > 0
          ? "linear-gradient(135deg, #fef2f2, #fee2e2)"
          : "linear-gradient(135deg, #f0fdf4, #dcfce7)",
      border: shortageWeight > 0 ? "#fca5a5" : "#6ee7b7",
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Space align="center" size={8}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/rakes")}
            >
              Back
            </Button>
            <div>
              <Title level={4} className="page-title" style={{ margin: 0 }}>
                <ContainerOutlined style={{ marginRight: 8, color: "#1677ff" }} />
                Rake {rakeMaster.rrNumber || rakeMaster.rakeMasterId}
              </Title>
              <Text className="page-subtitle">
                Master record and wagon-level details
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={reportLoading}
              onClick={handleDownloadReport}
            >
              Download Report
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchRake}>
              Refresh
            </Button>
            <Button
              icon={<FolderOpenOutlined />}
              onClick={() => setDocsOpen(true)}
            >
              Rake Documents
            </Button>
            {rakeMaster.s3Url && (
              <a
                href={rakeMaster.s3Url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button icon={<LinkOutlined />}>View Source</Button>
              </a>
            )}
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          </Space>
        </Col>
      </Row>

      <Card
        style={{ marginBottom: 20, borderRadius: 12 }}
        styles={{ body: { padding: 20 } }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2, md: 3 }}
          size="small"
          labelStyle={{ color: "#64748b", fontSize: 12 }}
          contentStyle={{ fontSize: 13 }}
        >
          <Descriptions.Item
            label={
              <>
                <NumberOutlined /> RR Number
              </>
            }
          >
            {rakeMaster.rrNumber || "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <NumberOutlined /> FNR Number
              </>
            }
          >
            {rakeMaster.fnrNumber || "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <BankOutlined /> Company
              </>
            }
          >
            {rakeMaster.company ? (
              <Tag color="purple">{rakeMaster.company}</Tag>
            ) : (
              "--"
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined /> Rake Date
              </>
            }
          >
            {rakeMaster.date
              ? moment(rakeMaster.date).format("MMM D, YYYY")
              : "--"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined /> Uploaded
              </>
            }
          >
            {moment(rakeMaster.createdAt).format("MMM D, YYYY")} (
            {moment(rakeMaster.createdAt).fromNow()})
          </Descriptions.Item>
          <Descriptions.Item label="Rake ID">
            <Text code style={{ fontSize: 12 }}>
              {rakeMaster.rakeMasterId}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {summaryCards.map((card) => (
          <Col key={card.label} xs={12} sm={8} md={4}>
            <Card
              className="stat-card"
              style={{ background: card.bg, border: `1px solid ${card.border}` }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: card.color,
                  lineHeight: 1.1,
                }}
              >
                {card.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#64748b",
                  fontWeight: 500,
                  marginTop: 4,
                }}
              >
                {card.label}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <RakeDriversList rakeMasterId={rakeMaster.rakeMasterId} />

      <Card
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: "0 24px 24px" } }}
      >
        <Tabs
          defaultActiveKey="trips"
          items={[
            {
              key: "trips",
              label: (
                <Space size={6}>
                  <NodeIndexOutlined />
                  Trips
                </Space>
              ),
              children: (
                <div style={{ paddingTop: 4 }}>
                  <TripListComponent rakeMasterId={rakeMaster.rakeMasterId} />
                </div>
              ),
            },
            {
              key: "wagons",
              label: (
                <Space size={6}>
                  <ContainerOutlined />
                  Wagons
                  <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
                    {rakeDetails.length}
                  </Tag>
                </Space>
              ),
              children: (
                <Table<IRakeDetail>
                  rowKey={(r) => `${r.rakeMasterId}-${r.sno}`}
                  size="middle"
                  columns={columns as any}
                  dataSource={rakeDetails}
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ["20", "50", "100"],
                    showTotal: (total, range) => (
                      <Text style={{ color: "#64748b", fontSize: 13 }}>
                        Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                        <Text strong>{total}</Text> wagon{total !== 1 ? "s" : ""}
                      </Text>
                    ),
                  }}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <div style={{ padding: "40px 0" }}>
                        <ContainerOutlined
                          style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                        />
                        <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                          No wagon details
                        </div>
                      </div>
                    ),
                  }}
                />
              ),
            },
            {
              key: "vehicles",
              label: (
                <Space size={6}>
                  <CarOutlined />
                  Vehicles
                  <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
                    {vehicleSummaries.length}
                  </Tag>
                </Space>
              ),
              children: uploadsLoading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spin />
                </div>
              ) : vehicleSummaries.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No vehicle uploads recorded during this rake operation"
                  style={{ padding: 40 }}
                />
              ) : (
                <Row gutter={[12, 12]} style={{ paddingTop: 4 }}>
                  {vehicleSummaries.map((v) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={v.vehicleNumber}>
                      <Card
                        hoverable
                        onClick={() => openVehicleModal(v.vehicleNumber)}
                        styles={{ body: { padding: 14 } }}
                        style={{ borderRadius: 10, border: "1px solid #e2e8f0" }}
                      >
                        <Space align="center" size={10} style={{ marginBottom: 8, width: "100%" }}>
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 10,
                              background: "#eff6ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#1677ff",
                              fontSize: 18,
                              flexShrink: 0,
                            }}
                          >
                            <CarOutlined />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Text strong style={{ fontSize: 14, color: "#1f2937", display: "block" }} ellipsis>
                              {v.vehicleNumber}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                              <FileTextOutlined style={{ marginRight: 4 }} />
                              {v.count} upload{v.count === 1 ? "" : "s"}
                            </Text>
                          </div>
                        </Space>
                        <Space size={4} wrap style={{ marginBottom: 6 }}>
                          {Object.entries(v.docTypes).map(([t, c]) => {
                            const cfg = getDocTypeConfig(t);
                            return (
                              <Tag key={t} color={cfg.color} style={{ margin: 0, borderRadius: 6, fontSize: 10 }}>
                                {cfg.label}: {c}
                              </Tag>
                            );
                          })}
                        </Space>
                        <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                          {dayjs(v.firstAt).format("MMM D, h:mm A")} →{" "}
                          {dayjs(v.lastAt).format("MMM D, h:mm A")}
                        </Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ),
            },
          ]}
        />
      </Card>

      <VehicleUploadsModal
        open={vehicleModalOpen}
        rakeMasterId={rakeMaster.rakeMasterId}
        vehicleNumber={selectedVehicle}
        onClose={closeVehicleModal}
      />

      <RakeDocumentsModal
        open={docsOpen}
        rakeMasterId={rakeMaster.rakeMasterId}
        onClose={() => setDocsOpen(false)}
      />
    </div>
  );
};

export default RakeDetailsComponent;
